import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Save,
  Edit
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import type { User } from '../types';

const Profile: React.FC = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Email update state
  const [email, setEmail] = useState('');
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  // Password change state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileData = await usersAPI.getProfile();
      setUser(profileData);
      setEmail(profileData.email);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailUpdate = async () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setEmailSaving(true);
      setError(null);
      
      const result = await usersAPI.updateProfile(email);
      setSuccess(result.message);
      setEmailEditing(false);
      
      // Update local user state
      if (user) {
        setUser({ ...user, email: result.email });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update email');
    } finally {
      setEmailSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All password fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    try {
      setPasswordSaving(true);
      setError(null);
      
      const result = await usersAPI.changePassword(currentPassword, newPassword);
      setSuccess(result.message);
      setPasswordDialog(false);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleCancelEmailEdit = () => {
    setEmailEditing(false);
    setEmail(user?.email || '');
    setError(null);
  };

  const handleCancelPasswordChange = () => {
    setPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        <Alert severity="error">
          Failed to load profile information.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Account Information
          </Typography>

          {/* Username (read-only) */}
          <Box display="flex" alignItems="center" mb={3}>
            <Person sx={{ mr: 2, color: 'text.secondary' }} />
            <Box flexGrow={1}>
              <Typography variant="body2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body1">
                {user.username}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Email (editable) */}
          <Box display="flex" alignItems="center" mb={3}>
            <Email sx={{ mr: 2, color: 'text.secondary' }} />
            <Box flexGrow={1}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email Address
              </Typography>
              {emailEditing ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    size="small"
                    fullWidth
                    placeholder="Enter email address"
                  />
                  <Button
                    onClick={handleEmailUpdate}
                    disabled={emailSaving}
                    variant="contained"
                    size="small"
                    startIcon={emailSaving ? <CircularProgress size={16} /> : <Save />}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={handleCancelEmailEdit}
                    disabled={emailSaving}
                    variant="outlined"
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body1">
                    {user.email}
                  </Typography>
                  <IconButton
                    onClick={() => setEmailEditing(true)}
                    size="small"
                    title="Edit email"
                  >
                    <Edit />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Password Change */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Lock sx={{ mr: 2, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Password
                </Typography>
                <Typography variant="body1">
                  ••••••••
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={() => setPasswordDialog(true)}
              variant="outlined"
              startIcon={<Lock />}
            >
              Change Password
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog} onClose={handleCancelPasswordChange} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              helperText="Must be at least 6 characters long"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPasswordChange} disabled={passwordSaving}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordChange}
            disabled={passwordSaving}
            variant="contained"
            startIcon={passwordSaving ? <CircularProgress size={16} /> : <Lock />}
          >
            {passwordSaving ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
